import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ListItemText from '@mui/material/ListItemText';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import Checkbox from '@mui/material/Checkbox';


import axios from 'axios';
import { NeedUnassignedList } from './need-unassigned-list';
import SelectedCasesList from '../components/selected-cases-list';

import { CONFIG } from "../../../../../config";
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';

import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import { toast } from 'react-hot-toast';
import customToast from '../../../../components/toast-modified';


import dayjs from 'dayjs';
import { handleModalClose } from '../../../../../utility/modal-close-handler';
import ShiftsList from '../components/shifts-list';
import ShiftUserList from '../components/shift-user-list';
import CategorisedCasesList from './categorised-cases-list';
import UnallocatedCasesList from './unallocated-cases-list';

const API_HOST = CONFIG.modules["accounts"].host;
const API_HOST_CRM = CONFIG.modules["crm"].host;


// Convert dayjs date to only time
const convertDayjsToTime = (date) => {
  return dayjs(date).format("HH:mm");
}

/**
 * Dialog component for assigning selected cases to agents or shifts.
 * Allows selecting shifts, agents, performing final checks, and handling assignment logic.
 *
 * @param {string | undefined} props.type - The type of the dialog. This will be used to send the final check.
 * @param {object} props - The component props.
 * @param {boolean} props.open - Controls the visibility of the dialog. True makes the dialog visible, false hides it. Passed from the parent component's state.
 * @param {Function} props.handleClose - Function to call when the dialog should be closed, typically by clicking the close button or outside the dialog. Used to update the parent component's state that controls the `open` prop.
 * @param {Map<string, object>} props.staticSelectedRows - Map containing the cases selected for assignment (key: case ID, value: case object). This map remains static even if the main list updates in the parent, ensuring the selection persists during the assignment process within this dialog. Used to display the list of selected cases and provide their IDs for API calls like final checks and assignment.
 * @param {Function} props.setStaticSelectedRows - Function provided by the parent component to update its `staticSelectedRows` state map. Used by this dialog to modify the selection, e.g., removing cases after successful assignment or if they fail checks.
 * @param {string|null} props.activeSelectedAssign - Determines the current view/mode within the dialog (e.g., 'selected-agent', 'agent-auto', or null for the initial view). Controls which part of the dialog's UI is rendered (e.g., shift selection, agent selection, completion message). Managed by the parent component.
 * @param {Function} props.setActiveSelectedAssign - Function provided by the parent component to update its `activeSelectedAssign` state, allowing navigation between different views/steps within this dialog.
 * @param {Map<string, object>} props.needToBeUnassignedCases - Map containing cases that need to be unassigned before assignment can proceed (key: case ID, value: case object). Populated if a check reveals selected cases are already assigned elsewhere. Used to display the `NeedUnassignedList` component if not empty. Passed from the parent component.
 * @param {Function} props.handleRemoveStaticSelection - Function provided by the parent component to remove a specific case from its `staticSelectedRows` map. Called when a user deselects a case from lists displayed within this dialog (e.g., `CategorisedCasesList`, `UnallocatedCasesList`).
 * @param {boolean} props.assignSelectedCompleted - Flag indicating if the assignment process for selected cases has completed successfully. Used to display completion messages or potentially alter UI states after a successful assignment API call. Managed by the parent component.
 * @param {boolean} props.assigningSelected - Flag indicating if the assignment process for selected cases is currently in progress. Used to disable buttons and show loading indicators during assignment API calls to prevent duplicate actions. Managed by the parent component.
 * @param {Function} props.setAssigningSelected - Function provided by the parent component to update its `assigningSelected` state, typically set to true before an assignment API call and false after the call completes (success or failure).
 * @param {Function} props.setAssignSelectedCompleted - Function provided by the parent component to update its `assignSelectedCompleted` state, typically set to true upon successful assignment API response.
 * @param {object} props.selectedAgents - Object containing the agents selected for assignment (key: agent ID, value: agent object). Used in the 'selected-agent' view (legacy, potentially replaced by `selectedUsers` state) to track which agents the cases will be assigned to. Passed to the assignment API. Managed by the parent component.
 * @param {Function} props.setSelectedAgents - Function provided by the parent component to update its `selectedAgents` state object when users check/uncheck agents in the list (legacy view).
 * @param {Array<object>} props.users - Array of all user objects available in the system, passed down from the Redux store via the parent component. Used to display the list of agents for selection in the legacy 'selected-agent' view.
 * @param {Function} props.getList - Function provided by the parent component (`active.js`) to refresh its main list of cases. Called after assignment or unassignment operations within this dialog to ensure the main grid reflects the latest case statuses and allocations.
 */
export default function SelectedAssignDialog({
  type,
  open,
  handleClose,
  staticSelectedRows,
  setStaticSelectedRows,
  activeSelectedAssign,
  setActiveSelectedAssign,
  // assignSelectedCases,
  // isFetchingSelectedCasesDetails,
  needToBeUnassignedCases,
  // setNeedToBeUnassignedCases,
  // unassignSelectedRows,
  // isUnassigning,
  handleRemoveStaticSelection,
  assignSelectedCompleted,
  assigningSelected,
  setAssigningSelected,
  setAssignSelectedCompleted,
  selectedAgents,
  setSelectedAgents,
  users,
  getList
}) {

  // Current day use memo
  const currentDay = useMemo(() => {
    return {
      formatted: dayjs().format('dddd, MMMM D, YYYY'),
      dayjs: dayjs(),
      dayjsTime: dayjs().format('HH:mm')
    }
  }, []);

  const {
    Shifts,
    Campaigns,
    Languages
  } = useSelector((state) => state.user || {});

  /**
   * This will be an array of _ids
   */
  const languageSearchArray = useMemo(() => {
    if (Languages && Languages.length > 0) {
      return Languages.map((l) => l._id);
    }
    return [];
  }, [Languages]);

  // Loader state until the redux state is loaded
  const [reduxLoading, setReduxLoading] = useState(true);

  // Making sure the Shifts and Campaigns are loaded
  useEffect(() => {
    if (Shifts && Campaigns) {
      setReduxLoading(false);
    }
  }, [Shifts, Campaigns]);

  // Making sure the ECD Campaign is loaded
  const ECDCampaign = useMemo(() => {
    if (Campaigns.length > 0) {
      return Campaigns.find((c) => c.name === "ECD");
    }
    return null;
  }, [Campaigns]);


  /**
   * State to handle the toggle button group
   * default should be - shift
   * can be changed to - agent
   */
  const [toggleButtonMode, setToggleButtonMode] = useState("shift");

  const handleToggleButtonModeChange = (e, value) => {
    // Need to do this because the state is in the parent component which is here
    // and when the mode changes need to reset the selected users
    setSelectedUsers([]);

    if (value === "shift") {
      setQueueCases([]);
      setPendingCases([]);
      setOtherStatusCases([]);
      setPendingTodayCases([]);
      setLanguageIssuesCases([]);
      setCallDoneButNotDue([]);
      setCallDoneCases([]);
      setScheduledLaterCases([]);
      setLimitReachedCases([]);
      setSameContactCases([]);
    }

    if (value !== null) {
      setToggleButtonMode(value);
    }

  }

  /**
   * State to handle the selected shift
   * 
   * shape: {
   * shift_id: shiftData
   * }
   */
  const [selectedShift, setSelectedShift] = useState(null);


  /**
   * Local State for the shifts that are available for the current day
   */
  const [shiftsForCurrentDayLoading, setShiftsForCurrentDayLoading] = useState(false);
  const [shiftsForCurrentDay, setShiftsForCurrentDay] = useState([]);

  /**
   * State to store the users in the shift
   */
  const [usersInShiftLoading, setUsersInShiftLoading] = useState(false);
  const [usersInShift, setUsersInShift] = useState([]);


  /**
   * State to store the selected agent
   */
  const [selectedUsers, setSelectedUsers] = useState([]);

  /**
   * Loading state for assigning
   */
  const [isAssigning, setIsAssigning] = useState(false);

  /**
   * States for the different status cases based on final check
   */
  const [finalCheckLoading, setFinalCheckLoading] = useState(false);
  const [queueCases, setQueueCases] = useState([]);
  const [pendingCases, setPendingCases] = useState([]);
  const [otherStatusCases, setOtherStatusCases] = useState([]);
  const [pendingTodayCases, setPendingTodayCases] = useState([]);
  const [languageIssuesCases, setLanguageIssuesCases] = useState([]);
  const [callDoneCases, setCallDoneCases] = useState([]);
  const [callDoneButNotDue, setCallDoneButNotDue] = useState([]);
  const [scheduledLaterCases, setScheduledLaterCases] = useState([]);
  const [limitReachedCases, setLimitReachedCases] = useState([]); // Meaning agent limit was exceeded so they are not allocated
  const [sameContactCases, setSameContactCases] = useState([]);

  /**
   * State for setting overallocate members
   */
  const [overAllocateUserMap, setOverAllocateUserMap] = useState(new Map());

  const addOverAllocateMember = (user) => {
    setOverAllocateUserMap((prev) => {
      const newMap = new Map(prev);
      newMap.set(user._id, user);
      return newMap;
    });
  }

  const removeOverAllocateMember = (user) => {
    setSelectedUsers([]);
    setOverAllocateUserMap((prev) => {
      const newMap = new Map(prev);
      newMap.delete(user._id);
      return newMap;
    });
  }

  // Api Method to get the shifts for the current day
  const getShiftsForCurrentDay = async () => {
    try {
      setShiftsForCurrentDayLoading(true);
      const response = await axios.get(API_HOST + "/shifts-for-current-day", {
        params: {
          campaign: ECDCampaign._id
        }
      });

      setShiftsForCurrentDay(response.data.shifts);
      setShiftsForCurrentDayLoading(false);
    } catch (e) {
      console.log(e);
      setShiftsForCurrentDayLoading(false);
    }
  }

  // Side effect that runs when the selected shift changes
  useEffect(() => {
    if (selectedShift) {
      getUsersFromShifts();
    }
  }, [selectedShift]);

  // Api Method to get the users from the shifts
  const getUsersFromShifts = async () => {
    try {
      setUsersInShiftLoading(true);

      // Selected Shape is a map type object
      const selectedShiftId = Object.keys(selectedShift)[0];
      const response = await axios.get(API_HOST + "/users-from-shifts", {
        params: {
          shift: selectedShiftId,
          schedule: selectedShift[selectedShiftId].schedule,
          campaign: ECDCampaign._id
        }
      });

      console.log(response.data.usersInShift, "Users in shift");

      // Check if the users in shift exists
      if (response.data.usersInShift) {
        // users in shift is an array that contains user property 
        // we need to get the users from the users array

        // check the length
        if (response.data.usersInShift.length > 0) {
          const modifiedUsersInShift = response.data.usersInShift.map((shiftWithUser) => {

            return shiftWithUser
          });

          setUsersInShift(modifiedUsersInShift);
        } else {
          setUsersInShift([]);
        }
      }

    } catch (e) {
      console.log(e);
    } finally {
      setUsersInShiftLoading(false);
    }
  }

  // Api Method to get do a final check on the cases and categorise them based on status
  const getFinalCheckCases = async () => {
    try {
      setFinalCheckLoading(true);
      const response = await axios.post(API_HOST_CRM + "/cases/cases-by-caseids-categorically", {
        cases: Array.from(staticSelectedRows.values()).map(
          (n) => n._id,
        ),
        type: type
      });

      /**
       * Shape of the response will be like this:
       * {
       *  success: boolean,
       * queueCases: Array,
       * pendingCases: Array,
       * otherStatusCases: Array
       * }
       */

      if (response.data && response.data.success) {
        setQueueCases(response.data.queueCases);
        setPendingCases(response.data.pendingCases);
        setOtherStatusCases(response.data.otherStatusCases);
        setCallDoneCases(response.data.callDoneCases);
        setCallDoneButNotDue(response.data.callDoneButNotDue);
        setScheduledLaterCases(response.data.scheduledLaterCases);
        setSameContactCases(response.data.sameContactCases || []);
        // We need to remove the matching motherids of pendingTodayCases from static selected rows

        let newStaticSelectedRows = new Map(staticSelectedRows);

        // Remove Pending Today Cases
        if (response.data.pendingTodayCases) {

          if (type !== "in-queue") {
            if (response.data.pendingTodayCases.length > 0) {
              customToast.success("There were cases that were assigned today and still in pending. Please assign them from allocations module.", {
                position: "top-center",
                duration: 3400
              });
            }
            response.data.pendingTodayCases.forEach((caseData) => {
              newStaticSelectedRows.delete(caseData._id);
            });
          } else {


          }
        } else {
          setPendingTodayCases([]);
        }

        if (response.data.scheduledLaterCases) {
          response.data.scheduledLaterCases.forEach((caseData) => {
            newStaticSelectedRows.delete(caseData._id);
          });

          if (type !== "in-queue") {
            if (response.data.scheduledLaterCases.length > 0) {
              customToast.success("There were cases that were scheduled later and still in pending. Please assign them from allocations module.", {
                position: "top-center",
                duration: 3400
              });
            }
          }
        } else {
          setScheduledLaterCases([]);
        }

        // Remove call done but not due yet
        if (response.data.callDoneButNotDue) {
          response.data.callDoneButNotDue.forEach((caseData) => {
            newStaticSelectedRows.delete(caseData._id);
          });

          if (type !== "in-queue") {
            if (response.data.callDoneButNotDue.length > 0) {
              customToast.success("There were cases that were 'Call done' but not yet due. Please assign them from allocations module.", {
                position: "top-center",
                duration: 3400
              });
            }
          }
        } else {
          setCallDoneButNotDue([]);
        }

        if (response.data.sameContactCases) {
          response.data.sameContactCases.forEach((caseData) => {
            // We cannot remove using the id because it will be a different case with the same contact
            // we have to find the same contact case in the static selected rows
            // and remove it
            // Array.from(newStaticSelectedRows.values()).forEach((selectedCase) => {
            //   if (selectedCase.contact === caseData.contact) {
            //     newStaticSelectedRows.delete(selectedCase._id);
            //   }
            // })
          });

          if (type !== "in-queue") {
            if (response.data.sameContactCases.length > 0) {
              customToast.success("There were cases that were same contact. Please assign them to the same telecaller by specifically selecting that single agent.", {
                position: "top-center",
                duration: 3400
              });
            }
          }
        } else {
          setSameContactCases([]);
        }

        setStaticSelectedRows(newStaticSelectedRows);

        setPendingTodayCases(response.data.pendingTodayCases);
      }
      console.log(response.data, "Response from the server");
    } catch (e) {
      console.log(e);
    } finally {
      setFinalCheckLoading(false);
    }
  }

  const displayFinalCheckCases = useMemo(() => {
    return queueCases.length > 0 || pendingCases.length > 0 || pendingTodayCases.length > 0 || scheduledLaterCases.length > 0 || otherStatusCases.length > 0 || callDoneButNotDue.length > 0 || callDoneCases.length > 0 || sameContactCases.length > 0;
  }, [queueCases, pendingCases, pendingTodayCases, scheduledLaterCases, otherStatusCases, toggleButtonMode, callDoneButNotDue, callDoneCases, sameContactCases]);

  // Method to handle the shift selection
  // This will rerun an useEffect that will get the users for that shift
  const handleShiftSelection = (shift) => {
    if (usersInShiftLoading) {
      return;
    }
    setSelectedShift({ [`${shift._id}`]: shift });
  }

  /**
   * Method to assign the cases to the selected agent
   */
  const assignCasesToSelectedAgent = async () => {
    try {
      // TODO: Change it later but only assign to 1 agent
      if (selectedUsers.length === 0) {
        toast.error("Please select an agent", {
          position: "top-center",
          duration: 1331
        });
        return;
      }

      if (selectedUsers.length > Array.from(staticSelectedRows.values()).length) {
        toast.error("Cases are less then the selected users", {
          position: "top-center",
          duration: 1331
        });
        return;
      }

      if (overAllocateUserMap.size > 0 && selectedUsers.length > 1) {
        toast.error("Overallocate is true but more than one agent is provided", {
          position: "top-center",
          duration: 1331
        });
        return;
      }

      // This will be the id of the user 

      const selectedShiftId = Object.keys(selectedShift)[0];
      try {
        setIsAssigning(true);
        const resp = await axios.post(API_HOST_CRM + "/allocate-cases-to-selected-agents", {
          cases: Array.from(staticSelectedRows.values()).map(
            (n) => n._id,
          ),
          agents: [...selectedUsers],
          shift: selectedShiftId,
          campaign: ECDCampaign._id,
          overallocate: overAllocateUserMap.size > 0 ? true : false
        });

        console.log(resp.data, "Response from the server");

        if (resp.data && resp.data.success) {

          // If there will be unallocated cases, meaning couldn't be allocated 
          // it will be present in the array 
          const unallocatedCases = resp.data.result.unallocatedCases;

          // if(unallocatedCases.length > 0)
          let newStaticSelectedRows = new Map(staticSelectedRows);
          let unallocatedIds = new Map()
          if (unallocatedCases.length > 0) {
            toast.error("Some cases were not allocated. Please try assigning them again.", {
              position: "top-center",
              duration: 1331
            });

            unallocatedCases.forEach(ucases => {
              unallocatedIds.set(ucases._id, ucases)
            });
          }

          // keep these in static selectRows
          setStaticSelectedRows(unallocatedIds);

          // need to only keep the unallocated cases in the queue, pending and other status cases
          setQueueCases(queueCases.filter(qc => unallocatedIds.has(qc._id)));
          setPendingCases(pendingCases.filter(pc => unallocatedIds.has(pc._id)));
          setOtherStatusCases(otherStatusCases.filter(osc => unallocatedIds.has(osc._id)));

          if (resp.data.result.languageIssuesCases) {
            setLanguageIssuesCases(resp.data.result.languageIssuesCases);
          }

          if (resp.data.result.limitReachedCases) {
            setLimitReachedCases(resp.data.result.limitReachedCases);
          }

          setAssignSelectedCompleted(true);
          // setStaticSelectedRows(new Map());
          getList(true);
          setSelectedUsers([]);
          await getUsersFromShifts();
          toast.success("Cases assigned successfully", {
            position: "top-center",
            duration: 1331
          });
        }
      } catch (e) {
        console.log(e);
        toast.error("Failed to assign cases", {
          position: "top-center",
          duration: 1331
        });
      } finally {
        setIsAssigning(false);
      }

    } catch (e) {
      console.log(e);
    }
  }
  useEffect(() => {
    if (ECDCampaign) {
      getShiftsForCurrentDay();
    }
  }, [ECDCampaign]);

  const onClose = (event, reason) => {
    handleModalClose(event, reason, handleClose);
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="selected-assign-dialog-title"
      aria-describedby="selected-assign-dialog-description"
      maxWidth="md"
      // fullWidt
      fullScreen
    >
      <DialogTitle id="selected-assign-dialog-title" sx={{ m: 0, p: 2 }}>
        Assign selected ({staticSelectedRows.size}) cases
        <IconButton
          disabled={isAssigning}
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box>
          {!activeSelectedAssign && (
            <Stack direction="column" spacing={2}>
              <Stack>
                <Stack>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    Current Day: {currentDay.formatted}
                  </Typography>
                </Stack>
                <Stack component={Paper} sx={{ p: 1 }} rowGap={1}>
                  <Stack>
                    <ToggleButtonGroup
                      fullWidth p={0} sx={{
                        maxHeight: 30,
                      }}
                      value={toggleButtonMode}
                      onChange={handleToggleButtonModeChange}
                      exclusive
                    >
                      <ToggleButton value="shift">
                        Shifts
                      </ToggleButton>
                      <ToggleButton value="agent"
                        disabled={usersInShift.length === 0}
                      >
                        Agents
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                  {toggleButtonMode === "shift" && (
                    <Stack direction="column" spacing={2} flexWrap="wrap" rowGap={1}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        Available Shifts:
                      </Typography>
                      <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={1}>
                        {shiftsForCurrentDayLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          shiftsForCurrentDay.length > 0 ? (
                            <ShiftsList
                              shifts={shiftsForCurrentDay}
                              currentDay={currentDay}
                              selectedShift={selectedShift}
                              handleShiftSelection={handleShiftSelection}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No shifts available for today
                            </Typography>
                          )
                        )}
                      </Stack>
                      <Stack>
                        {
                          usersInShift.length > 0 && (
                            <Button variant="outlined" size="small" onClick={() => {
                              getUsersFromShifts();
                              setToggleButtonMode("agent");
                            }}>
                              <ArrowOutwardIcon sx={{ fontSize: "1.2em" }} /> &nbsp;
                              Select an agent
                            </Button>
                          )
                        }
                      </Stack>
                    </Stack>
                  )}
                  {toggleButtonMode === "agent" && (
                    <Stack rowGap={2}>
                      <Stack>
                        <Button variant="outlined" size="small" onClick={() => {
                          handleToggleButtonModeChange(null, "shift");
                        }}>
                          <ArrowBackIcon sx={{ fontSize: "1.2em" }} /> &nbsp;
                          Back
                        </Button>
                      </Stack>

                      {/* Buttons for fetching case details */}
                      <Stack
                        component={Paper}
                        p={2}
                        direction="row"
                        spacing={2}
                        flexWrap="wrap"
                        rowGap={1}
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Stack>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            Available Agents:
                          </Typography>
                        </Stack>
                        <Stack rowGap={1}>
                          <Stack direction="row" alignItems="center" spacing={1}>

                            <Button
                              disabled={finalCheckLoading}
                              variant="outlined"
                              size="small"
                              onClick={() => {
                                getFinalCheckCases()
                              }}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Stack>
                                  {finalCheckLoading && <CircularProgress size={20} />}
                                </Stack>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  Final Check
                                </Typography>
                              </Stack>
                            </Button>
                          </Stack>
                          <Button
                            disabled={selectedUsers.length === 0 || isAssigning || !displayFinalCheckCases}

                            variant="outlined" size="small" onClick={() => {
                              assignCasesToSelectedAgent();
                            }}>
                            {
                              isAssigning ? "Assigning..." : "Assign"
                            }
                          </Button>
                        </Stack>
                      </Stack>
                      {/* User List */}
                      <Stack>
                        {
                          usersInShiftLoading && (
                            <CircularProgress size={20} />
                          )
                        }
                        {
                          usersInShift.length > 0 && (
                            <ShiftUserList
                              users={usersInShift}
                              languages={languageSearchArray}
                              selectedUsers={selectedUsers}
                              setSelectedUsers={setSelectedUsers}
                              overAllocateUserMap={overAllocateUserMap}
                              addOverAllocateMember={addOverAllocateMember}
                              removeOverAllocateMember={removeOverAllocateMember}
                            />
                          )
                        }
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              </Stack>
              {/* Render Language Issues Cases List And Limit Reached Cases List */}
              {(languageIssuesCases.length > 0 || limitReachedCases.length > 0) && toggleButtonMode === "agent" && (
                <Paper elevation={3} sx={{
                  p: 1
                }}>
                  <UnallocatedCasesList
                    onRemoveCaseSelection={handleRemoveStaticSelection}
                    languageIssuesCases={languageIssuesCases}
                    limitReachedCases={limitReachedCases}
                  />
                </Paper>
              )}
              {displayFinalCheckCases && toggleButtonMode === "agent" && (
                <Paper elavation={3} sx={{
                  p: 1
                }}>
                  <CategorisedCasesList
                    onRemoveCaseSelection={handleRemoveStaticSelection}
                    queueCases={queueCases}
                    pendingCases={pendingCases}
                    otherStatusCases={otherStatusCases}
                    pendingTodayCases={pendingTodayCases}
                    callDoneButNotDue={callDoneButNotDue}
                    callDoneCases={callDoneCases}
                    scheduledLaterCases={scheduledLaterCases}
                    sameContactCases={sameContactCases}
                  />
                </Paper>
              )}

              {/* {needToBeUnassignedCases.size > 0 && (
                                <NeedUnassignedList
                                    data={needToBeUnassignedCases}
                                    setData={setNeedToBeUnassignedCases}
                                    handleBack={() => setNeedToBeUnassignedCases(new Map())}
                                    unassign={unassignSelectedRows}
                                    unassigning={isUnassigning}
                                    onRemoveCaseSelection={handleRemoveStaticSelection}
                                />
                            )} */}
              {needToBeUnassignedCases.size === 0 && (
                <SelectedCasesList
                  staticSelectedRows={staticSelectedRows}
                  setStaticSelectedRows={setStaticSelectedRows}
                />
              )}
            </Stack>
          )}
          {activeSelectedAssign === "agent-auto" && (
            <Box>
              <Typography>
                {assignSelectedCompleted
                  ? "Assign Completed"
                  : assigningSelected
                    ? "Assigning....."
                    : ""}
              </Typography>
            </Box>
          )}
          {activeSelectedAssign === "selected-agent" && (
            <Box sx={{ ...(assigningSelected ? { pointerEvents: "none" } : {}) }}>
              <Box sx={{ mb: 1 }}>
                <Box
                  sx={{ cursor: "pointer", width: "fit-content" }}
                  onClick={() => setActiveSelectedAssign(null)}
                >
                  <ArrowBackIcon sx={{ fontSize: "1.2em" }} /> &nbsp;Back
                </Box>
              </Box>
              <Box sx={{ pl: 2 }}>
                <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontWeight: "bold" }}>Select Agents</Typography>
                  <Box sx={{ display: "flex" }}>
                    {`selected (${Object.values(selectedAgents).length})`}
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setAssigningSelected(true);
                        axios
                          .post(API_HOST_CRM + "/allocate-selected-selected", {
                            cases: Array.from(staticSelectedRows.values()).map(n => n._id),
                            agents: Object.values(selectedAgents).map(a => a._id),
                          })
                          .then((resp) => {
                            if (resp.data?.success) {
                              setAssigningSelected(false);
                              setAssignSelectedCompleted(true);
                              setStaticSelectedRows(new Map());
                              getList(true);
                            } else {
                              setAssigningSelected(false);
                            }
                          })
                          .catch((e) => {
                            setAssigningSelected(false);
                            console.log(e);
                          });
                      }}
                      size="small"
                      sx={{ width: "max-content", ml: 1 }}
                    >
                      <DoubleArrowIcon sx={{ fontSize: "1.2em" }} /> &nbsp;
                      {assignSelectedCompleted
                        ? "Assign Completed"
                        : assigningSelected
                          ? "Assigning....."
                          : "Assign"}
                    </Button>
                  </Box>
                </Box>
                {Array.isArray(users) &&
                  users
                    .filter(u => (u.roles || []).includes(process.env.REACT_APP_TELECALLER))
                    .map((u, i) => (
                      <ListItem key={i} disablePadding>
                        <ListItemButton>
                          <Checkbox
                            checked={!!selectedAgents[u._id]}
                            onChange={(e) => {
                              const obj = { ...selectedAgents };
                              if (e.target.checked) {
                                obj[u._id] = u;
                              } else {
                                delete obj[u._id];
                              }
                              setSelectedAgents(obj);
                            }}
                          />
                          <ListItemText
                            primaryTypographyProps={{ fontSize: ".8em" }}
                            primary={u.name}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isAssigning}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function isTimeBetween(time, startTime, endTime) {
  // Convert all times to minutes since midnight for comparison
  const getMinutesSinceMidnight = (timeObj) => {
    // Handle string format if needed
    if (typeof timeObj === 'string') {
      const parts = timeObj.split(':');
      return parseInt(parts[0]) * 60 + parseInt(parts[1]);
    }
    // Handle dayjs object
    return timeObj.hour() * 60 + timeObj.minute();
  };

  const timeMinutes = getMinutesSinceMidnight(time);
  const startMinutes = getMinutesSinceMidnight(startTime);
  const endMinutes = getMinutesSinceMidnight(endTime);

  // Handle cases that span midnight
  if (startMinutes > endMinutes) {
    return timeMinutes >= startMinutes || timeMinutes <= endMinutes;
  }

  return timeMinutes >= startMinutes && timeMinutes <= endMinutes;
}
